import React from 'react';
import { withRouter } from 'react-router';
import ButtonWithCount from 'chameleon/ui-stack/ui/assessments/assignment-status/ButtonWithCount';
import dayjs from 'dayjs';
import { LockedIcon } from '../status-icons/LockedIcon';
import { CheckmarkIcon } from '../status-icons/CheckmarkIcon';
import { AssignedIcon } from '../status-icons/AssignedIcon';
import { PrintIcon } from '../status-icons/PrintIcon';
import TextWithTooltip from 'chameleon/ui-stack/ui/assessments/text-with-tooltip';
import { computeAssignmentsReadyToScore } from 'chameleon/src/components/learnosity/utils/helpers';
import cn from 'classnames';

const AssignmentStatus = (props) => {
  const {
    id,
    status,
    progress,
    router,
    hasStudents,
    assignmentStartDate,
    assignmentEndDate,
    timezone,
    isComplete,
    submitted,
    totalSubmissions,
    assignmentURL,
    isUnassigned = false,
    isSPP,
    isPrint = false,
    hasAllowedLateSubmission,
  } = props;

  const assignmentInFuture =
    dayjs(assignmentStartDate).tz(timezone) &&
    dayjs(assignmentStartDate).tz(timezone).isAfter(dayjs().tz(timezone));
  const assignmentInPast =
    dayjs(assignmentEndDate).tz(timezone) &&
    dayjs(assignmentEndDate).tz(timezone).isBefore(dayjs().tz(timezone));
  const isAutoAssigned =
    assignmentStartDate === null && assignmentEndDate === null;
  const assignmentsReadyToScore = computeAssignmentsReadyToScore(progress);
  const isReadyToScore = assignmentsReadyToScore > 0;
  const tooltipTargetId = `${id}-assignment-status`;

  const isLocked = status === 'locked';

  const statusText = (text, icon) => {
    return (
      <span
        id={tooltipTargetId}
        className={cn(
          'align-middle mb-0 text-12',
          isUnassigned ? 'text-gray-800' : 'text-blue-700',
        )}
      >
        {icon}
        <span className={cn({ 'ml-1': icon })}>{text}</span>
      </span>
    );
  };

  const submittedPercent = Math.round((submitted * 100) / totalSubmissions);
  const manageAssignMentLink = (
    <TextWithTooltip
      text={statusText(`${submittedPercent}% Submitted`)}
      target={tooltipTargetId}
      tooltip="Manage Assignment"
      onClick={() => router.push(assignmentURL)}
      ariaLabel={`${submittedPercent} percent Submitted. Manage Assignment`}
      toolTipElementProps={{
        role: 'link',
      }}
    />
  );

  const renderSubmitted = manageAssignMentLink;
  const renderReadyToScore = isSPP ? (
    manageAssignMentLink
  ) : (
    <div className="w-1/2">
      <TextWithTooltip
        target={tooltipTargetId}
        tooltip="Score Submissions"
        onClick={() => router.push(assignmentURL)}
        ariaLabel={`${assignmentsReadyToScore} To Score. Manage Assignment`}
        toolTipElementProps={{
          role: 'link',
        }}
      >
        <ButtonWithCount
          onClick={() => router.push(assignmentURL)}
          count={assignmentsReadyToScore}
          text="To Score"
        />
      </TextWithTooltip>
    </div>
  );

  const renderLockedAssignment = (
    <TextWithTooltip
      text={<LockedIcon className="mr-1" />}
      tooltip="Manage Assignment"
      onClick={() => router.push(assignmentURL)}
      ariaLabel=""
    />
  );

  const renderCompletedAssignment = (
    <TextWithTooltip
      text={statusText('Complete', <CheckmarkIcon />)}
      target={tooltipTargetId}
      tooltip="Manage Assignment"
      onClick={() => router.push(assignmentURL)}
      ariaLabel="Complete. Manage Assignment"
      toolTipElementProps={{
        role: 'link',
      }}
    />
  );

  const renderPrintUncompletedAssignment = (
    <TextWithTooltip
      text={<PrintIcon className="mr-2" />}
      tooltip="Assigned in Print Format"
      onClick={() => router.push(assignmentURL)}
    />
  );
  let renderedStatus;

  if (!totalSubmissions) {
    renderedStatus = (
      <TextWithTooltip
        text={statusText('Assigned', <AssignedIcon />)}
        target={tooltipTargetId}
        tooltip="Manage Assignment"
        onClick={() => router.push(assignmentURL)}
        ariaLabel="Assigned. Manage Assignment"
        toolTipElementProps={{
          role: 'link',
        }}
      />
    );
  } else if (isComplete) {
    renderedStatus = renderCompletedAssignment;
  } else if (isPrint) {
    renderedStatus = (
      <div className="flex flex-row -ml-1 items-center">
        {renderPrintUncompletedAssignment}
        {renderSubmitted}
      </div>
    );
  } else if (isLocked) {
    renderedStatus = (
      <div className="flex flex-row -ml-1 items-center">
        {renderLockedAssignment}
        {isReadyToScore ? renderReadyToScore : renderSubmitted}
      </div>
    );
  } else if (isReadyToScore) {
    renderedStatus = renderReadyToScore;
  } else {
    renderedStatus = renderSubmitted;
  }

  // see https://academicmerit.atlassian.net/browse/FY-16327
  if (!hasStudents) {
    if (
      assignmentInFuture ||
      isAutoAssigned ||
      (assignmentInPast && !hasAllowedLateSubmission)
    ) {
      renderedStatus = renderLockedAssignment;
    }
    if (status === 'closed') {
      renderedStatus = renderCompletedAssignment;
    }
    if (isLocked && isPrint) {
      renderedStatus = renderPrintUncompletedAssignment;
    }
  }

  if (isUnassigned) {
    renderedStatus = (
      <div className="flex flex-row -ml-1 items-center">
        {statusText('Unassigned')}
      </div>
    );
  }

  return renderedStatus;
};

export default withRouter(AssignmentStatus);
